import React, { useEffect, useState, useContext, useRef } from "react"
import hotkeys from "hotkeys-js"

import SourceView from "./sources_page/source_view"
import { WebSource, ID } from "../../../model/types"
import { useNana } from "../../hooks/useNana"
import { DeviceContext, KEYS } from "../../providers/device_context"
import classNames from "classnames"

import SourcesSidebar from "./sources_page/sources_sidebar"
import SidebarResizeHandle, {
  loadSidebarWidth,
  saveSidebarWidth,
  sidebarWidthCss,
} from "./sources_page/sidebar_resize_handle"

export const SourcesPageState: {
  openSourceId: ID | null
} = {
  openSourceId: null,
}

export default function SourcesPage() {
  const { model, hub, commander } = useNana()

  const { platform } = useContext(DeviceContext)

  const savedWebSource = SourcesPageState.openSourceId
    ? model.getSource(SourcesPageState.openSourceId)
    : null

  const [openSource, setOpenSource1] = useState<WebSource | null>(
    savedWebSource
  )

  const setOpenSource = (source: WebSource | null) => {
    setOpenSource1(source)
    SourcesPageState.openSourceId = source?.id || null
  }

  const sidebarRef = useRef<HTMLDivElement>(null)
  // null means the default width; set once the user resizes the sidebar
  const [sidebarWidth, setSidebarWidth1] = useState<number | null>(loadSidebarWidth)
  const setSidebarWidth = (width: number | null) => {
    setSidebarWidth1(width)
    saveSidebarWidth(width)
  }

  useEffect(() => {
    const keys = [
      hub.on("model:source-deleted", ({ id }) => {
        if (openSource && id === openSource.id) setOpenSource(null)
      }),
      hub.on("model:source-updated", ({ id }) => {
        if (openSource && id === openSource.id)
          setOpenSource(model.getSource(id))
      }),
    ]
    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [openSource?.id])

  useEffect(() => {
    hotkeys(
      KEYS.undo[platform],
      { scope: "sources" },
      function (event, handler) {
        event.preventDefault()
        commander.undo()
      }
    )

    hotkeys(
      KEYS.redo[platform],
      { scope: "sources" },
      function (event, handler) {
        event.preventDefault()
        commander.redo()
      }
    )

    return () => {
      hotkeys.unbind(KEYS.undo[platform], "sources")
      hotkeys.unbind(KEYS.redo[platform], "sources")
    }
  }, [])

  hotkeys.setScope("sources")

  return (
    <div className="flex min-h-0 flex-auto pt-16 dark:bg-gray-950">
      <div
        ref={sidebarRef}
        className={classNames(
          "relative min-h-0 w-full flex-col justify-start border-r border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900 md:shrink-0",
          {
            "hidden md:flex": openSource,
            flex: !openSource,
            "md:w-2/5 md:max-w-xl": sidebarWidth == null,
            "md:w-[var(--sidebar-width)]": sidebarWidth != null,
          }
        )}
        style={
          sidebarWidth == null
            ? undefined
            : ({ "--sidebar-width": sidebarWidthCss(sidebarWidth) } as React.CSSProperties)
        }
      >
        <SourcesSidebar openSource={openSource} setOpenSource={setOpenSource} />
        <SidebarResizeHandle
          sidebarRef={sidebarRef}
          onResize={setSidebarWidth}
          onReset={() => setSidebarWidth(null)}
        />
      </div>
      <div
        className={classNames(
          "flex min-w-0 w-full flex-col items-left md:flex-1",
          {
            "hidden md:flex": !openSource,
          }
        )}
      >
        {!openSource && (
          <div className="flex justify-center">
            <div className="mt-24 text-sm text-gray-700 dark:text-gray-300 mx-auto prose dark:prose-invert prose-sm">
              <p>Manage your learning queue of sources here.</p>
              <p>
                Sources are reading material relevant to your learning
                interests.
              </p>
              <p>They can be:</p>
              <ul>
                <li>books</li>
                <li>papers</li>
                <li>blog posts</li>
                <li>articles</li>
                <li>videos</li>
                <li>presentations</li>
                <li>etc...</li>
              </ul>
              <p>
                Just add the URL and Nana will use AI to automatically fetch the
                title and author.
              </p>
              <p>
                Then take literature and mosaic notes from each source, and mark
                them as read.
              </p>
            </div>
          </div>
        )}
        {openSource && (
          <SourceView source={openSource} setOpenSource={setOpenSource} />
        )}
      </div>
    </div>
  )
}
